import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, MenuItem,
  TextField, Button, Link, Chip,
} from '@mui/material';
import { Home, Assignment, Print, NotificationsActive } from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

export default function BusManifest() {
  const navigate = useNavigate();
  const [buses, setBuses] = useState([]);
  const [faNo, setFaNo] = useState('');
  const [leg, setLeg] = useState('morning');
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [loading, setLoading] = useState(false);
  const [notifying, setNotifying] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notifyDelay = async () => {
    if (!faNo) return;
    setNotifying(true); setOk(''); setError('');
    try {
      const { data } = await axios.post(`${API}/api/fleet/notify-delay/${faNo}`, { leg }, { headers });
      setOk(`Delay notice sent to ${data.sent} guardian contact(s).`);
    } catch (err) { setError(err.response?.data?.error || 'Failed to notify'); }
    finally { setNotifying(false); }
  };

  useEffect(() => {
    axios.get(`${API}/api/fleet/buses`, { headers }).then(r => setBuses(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const loadManifest = async (bus = faNo, which = leg) => {
    if (!bus) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/fleet/manifest/${bus}?leg=${which}`, { headers });
      setManifest(data); setError('');
    } catch (err) { setError(err.response?.data?.error || 'Failed to load manifest'); }
    finally { setLoading(false); }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} className="no-print">
        <Box display="flex" alignItems="center"><Assignment sx={{ mr: 1, color: '#0277bd' }} /><Typography variant="h5" sx={{ color: '#0277bd' }}>Bus Manifest</Typography></Box>
        <IconButton onClick={() => navigate('/fleet')} title="Fleet Dashboard"><Home /></IconButton>
      </Box>

      <Card elevation={2} sx={{ mb: 2 }} className="no-print">
        <CardContent>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField select label="Bus" value={faNo} size="small" sx={{ minWidth: 240 }}
              onChange={e => { setFaNo(e.target.value); loadManifest(e.target.value, leg); }}>
              {buses.map(b => <MenuItem key={b.faNo} value={b.faNo}>{b.faName || b.faNo}</MenuItem>)}
            </TextField>
            <TextField select label="Trip" value={leg} size="small" sx={{ minWidth: 160 }}
              onChange={e => { setLeg(e.target.value); loadManifest(faNo, e.target.value); }}>
              <MenuItem value="morning">Morning (pickup)</MenuItem>
              <MenuItem value="evening">Evening (drop-off)</MenuItem>
            </TextField>
            <Button variant="outlined" startIcon={<Print />} disabled={!manifest} onClick={() => window.print()}>Print</Button>
            <Button variant="outlined" color="warning" startIcon={<NotificationsActive />}
              disabled={!manifest || !manifest.count || notifying} onClick={notifyDelay}>
              {notifying ? 'Sending…' : 'Notify delay'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')} className="no-print">{ok}</Alert>}
      {loading && <Box sx={{ textAlign: 'center', my: 3 }}><CircularProgress /></Box>}

      {manifest && !loading && (
        <Card elevation={3} id="manifest-print">
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{manifest.busName} — {manifest.leg === 'evening' ? 'Evening drop-off' : 'Morning pickup'}</Typography>
            <Box display="flex" gap={1} flexWrap="wrap" my={1}>
              {manifest.driver && <Chip label={`Driver: ${manifest.driver}`} size="small" />}
              {manifest.assistant && <Chip label={`Assistant: ${manifest.assistant}`} size="small" />}
              {manifest.capacity != null && <Chip label={`Capacity: ${manifest.capacity}`} size="small" variant="outlined" />}
              <Chip label={`${manifest.count} learner(s)`} size="small" color={manifest.capacity != null && manifest.count > manifest.capacity ? 'warning' : 'info'} />
            </Box>
            {manifest.learners.length === 0 ? <Typography color="text.secondary">No learners assigned to this bus for this trip.</Typography> : (
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell><strong>Adm No</strong></TableCell><TableCell><strong>Name</strong></TableCell><TableCell><strong>Class</strong></TableCell>
                  <TableCell><strong>Stage</strong></TableCell><TableCell><strong>Guardian(s)</strong></TableCell><TableCell className="no-print"><strong>Map</strong></TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {manifest.learners.map(l => (
                    <TableRow key={l.admNo}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{l.admNo}</TableCell>
                      <TableCell>{l.name}</TableCell>
                      <TableCell>{l.classCode}</TableCell>
                      <TableCell>{l.location}</TableCell>
                      <TableCell>{(l.guardians || []).map(g => `${g.name}${g.phone ? ` (${g.phone})` : ''}`).join('; ') || '—'}</TableCell>
                      <TableCell className="no-print">{l.mapLink ? <Link href={l.mapLink} target="_blank" rel="noreferrer">Map</Link> : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
